import { Loader2, Mic, MicOff, Square } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { Button } from "@bigpp/ui/components/button";
import { Tooltip } from "@bigpp/ui/components/tooltip";
import { cn } from "@bigpp/ui/lib/utils";

import { trpc } from "../trpc.ts";

// Record microphone audio via MediaRecorder, ship it to the server on stop,
// call OpenAI gpt-4o-mini-transcribe, and hand the resulting text to the
// composer. MediaRecorder works reliably in the Office task-pane webview
// (Edge WebView2 / WKWebView) where the Web Speech API is unreliable.

const pickMimeType = (): { mimeType?: string; filename: string } => {
  const rec = (globalThis as { MediaRecorder?: { isTypeSupported: (t: string) => boolean } })
    .MediaRecorder;
  if (!rec) return { filename: "audio.webm" };
  const candidates: { mimeType: string; filename: string }[] = [
    { mimeType: "audio/webm;codecs=opus", filename: "audio.webm" },
    { mimeType: "audio/webm", filename: "audio.webm" },
    { mimeType: "audio/mp4", filename: "audio.mp4" },
    { mimeType: "audio/ogg;codecs=opus", filename: "audio.ogg" },
  ];
  for (const c of candidates) {
    if (rec.isTypeSupported(c.mimeType)) return c;
  }
  return { filename: "audio.webm" };
};

const blobToBase64 = async (blob: Blob): Promise<string> => {
  const buf = await blob.arrayBuffer();
  const bytes = new Uint8Array(buf);
  let binary = "";
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunk));
  }
  return globalThis.btoa(binary);
};

type Props = {
  disabled?: boolean;
  onTranscript: (text: string) => void;
};

export function SpeechInput({ disabled, onTranscript }: Props) {
  const [state, setState] = useState<"idle" | "recording" | "transcribing">(
    "idle",
  );
  const [error, setError] = useState<string | null>(null);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const streamRef = useRef<MediaStream | null>(null);

  const transcribe = trpc.voice.transcribe.useMutation();

  useEffect(() => {
    return () => {
      recorderRef.current?.stop();
      for (const track of streamRef.current?.getTracks() ?? []) track.stop();
    };
  }, []);

  const supported =
    typeof globalThis.navigator?.mediaDevices?.getUserMedia === "function" &&
    typeof (globalThis as { MediaRecorder?: unknown }).MediaRecorder !== "undefined";
  if (!supported) return null;

  const ensureStream = async (): Promise<MediaStream> => {
    const existing = streamRef.current;
    if (existing?.getTracks().some((track) => track.readyState === "live")) {
      return existing;
    }
    for (const track of existing?.getTracks() ?? []) track.stop();
    const stream = await globalThis.navigator.mediaDevices.getUserMedia({
      audio: true,
    });
    streamRef.current = stream;
    return stream;
  };

  const start = async () => {
    setError(null);
    try {
      // Keep the stream open for the task-pane session so Office WebView only
      // prompts for mic access once, not on every Dictate press.
      const stream = await ensureStream();
      const { mimeType, filename } = pickMimeType();
      const rec = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
      recorderRef.current = rec;
      chunksRef.current = [];
      rec.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };
      rec.onstop = () => {
        void (async () => {
          const blob = new Blob(chunksRef.current, {
            type: rec.mimeType || mimeType || "audio/webm",
          });
          if (blob.size === 0) {
            setState("idle");
            return;
          }
          setState("transcribing");
          try {
            const base64 = await blobToBase64(blob);
            const res = await transcribe.mutateAsync({
              base64,
              mediaType: blob.type || "audio/webm",
              filename,
            });
            const text = res.text.trim();
            if (text) onTranscript(text);
          } catch (err) {
            setError(err instanceof Error ? err.message : String(err));
          } finally {
            setState("idle");
          }
        })();
      };
      rec.start();
      setState("recording");
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setState("idle");
    }
  };

  const stop = () => {
    recorderRef.current?.stop();
  };

  const busy = state !== "idle";

  const label = error
    ? `Speech: ${error}`
    : state === "recording"
      ? "Stop recording"
      : state === "transcribing"
        ? "Transcribing…"
        : "Dictate";

  return (
    <Tooltip label={label}>
    <Button
      type="button"
      variant="ghost"
      size="icon-sm"
      onClick={() => {
        if (state === "recording") stop();
        else if (state === "idle") void start();
      }}
      disabled={disabled || state === "transcribing"}
      aria-label={label}
      className={cn(
        state === "recording" && "text-[var(--color-destructive)]",
        state === "transcribing" && "text-[var(--color-muted-foreground)]",
      )}
    >
      {state === "recording" ? (
        <Square className="animate-pulse" />
      ) : state === "transcribing" ? (
        <Loader2 className="animate-spin" />
      ) : error ? (
        <MicOff />
      ) : (
        <Mic />
      )}
      {/* Reserve intent for CSS-in-JS; suppress unused warning */}
      <span className="sr-only">{busy ? "recording" : "idle"}</span>
    </Button>
    </Tooltip>
  );
}
